/**
 * App Catalogue — Features & Permissions page.
 *
 * Per app (selectable): edit the app-level entitlement
 * (anyOfRoles / anyOfRights / allOfRights) and the per-feature on/off
 * featureFlags, saving via `updateApp()`.
 *
 * The specific right that *gates a feature* is NOT stored in the manifest — it
 * is configured under /admin/permissions. We surface a helper note + link.
 *
 * Field styles mirror `AppManifestForm` (StringListEditor chips + feature-flag
 * switch rows).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconArrowLeft,
  IconShieldCheck,
  IconFlag,
  IconInfoCircle,
  IconPlus,
  IconX,
  IconDeviceFloppy,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Title,
  Text,
  Badge,
  Select,
  Button,
  Alert,
  Loader,
  Center,
  Divider,
  TextInput,
  Switch,
  Anchor,
  ActionIcon,
  toast,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { useApplicationCatalogueService } from '@actschurch/ccms-sdk';
import type { AppManifest } from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';

const CATALOGUE_QUERY_KEY = ['admin', 'app-catalogue', 'all'];

/** Deep clone a manifest into an independent editable draft. */
const cloneManifest = (m: AppManifest): AppManifest =>
  JSON.parse(JSON.stringify(m)) as AppManifest;

/** Chip list editor, mirrors AppManifestForm's StringListEditor. */
function StringListEditor({
  label,
  description,
  placeholder,
  values,
  onChange,
}: {
  label: string;
  description?: string;
  placeholder: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) {
      onChange([...values, v]);
    }
    setDraft('');
  };

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      {description && (
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      )}
      <Group gap="xs">
        <TextInput
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          style={{ flex: 1 }}
        />
        <ActionIcon variant="light" onClick={add} aria-label={`Add ${label}`}>
          <IconPlus size={16} />
        </ActionIcon>
      </Group>
      {values.length > 0 && (
        <Group gap={6} mt={4}>
          {values.map((v) => (
            <Badge
              key={v}
              variant="light"
              rightSection={
                <ActionIcon
                  size="xs"
                  variant="transparent"
                  color="gray"
                  onClick={() => onChange(values.filter((x) => x !== v))}
                  aria-label={`Remove ${v}`}
                >
                  <IconX size={10} />
                </ActionIcon>
              }
            >
              {v}
            </Badge>
          ))}
        </Group>
      )}
    </Stack>
  );
}

export default function FeaturesPermissionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();

  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AppManifest | null>(null);
  const [flagKey, setFlagKey] = useState('');
  const [saving, setSaving] = useState(false);

  const {
    data: apps = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: CATALOGUE_QUERY_KEY,
    queryFn: () => catalogueService.getAllApps(),
  });

  const selectData = useMemo(
    () =>
      (apps as AppManifest[]).map((a) => ({
        value: a.appId,
        label: `${a.displayName} (${a.appId})`,
      })),
    [apps]
  );

  const selectedApp = useMemo(
    () => (apps as AppManifest[]).find((a) => a.appId === selectedAppId) ?? null,
    [apps, selectedAppId]
  );

  // Seed an editable draft whenever the selected app changes.
  useEffect(() => {
    setDraft(selectedApp ? cloneManifest(selectedApp) : null);
    setFlagKey('');
  }, [selectedApp]);

  const featureFlags = draft?.featureFlags ?? {};
  const entitlement = draft?.entitlement ?? {
    anyOfRoles: [],
    anyOfRights: [],
    allOfRights: [],
  };

  const updateDraft = (patch: Partial<AppManifest>) => {
    setDraft((m) => (m ? { ...m, ...patch } : m));
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await catalogueService.updateApp(draft.appId, {
        ...draft,
        metadata: {
          ...draft.metadata,
          updatedAt: new Date().toISOString(),
        },
      });
      await queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY });
      toast.success(`${draft.displayName} updated`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update app');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_OVERVIEW)}
          >
            Back to overview
          </Button>
          <Title order={2} mt="xs">
            Features & Permissions
          </Title>
          <Text c="dimmed" size="sm">
            Edit an app's access gate (entitlement) and per-feature on/off toggles.
          </Text>
        </div>

        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
          Feature flags here only switch a feature ON or OFF. The specific{' '}
          <strong>right</strong> that gates who may use a feature is configured under{' '}
          <Anchor component={Link} to={ADMIN_ROUTES.PERMISSIONS}>
            Permissions
          </Anchor>
          .
        </Alert>

        {error ? (
          <Alert color="red" title="Service Unavailable">
            <Text size="sm">
              {error instanceof Error ? error.message : 'Failed to load the app catalogue.'}
            </Text>
            <Button
              variant="light"
              color="red"
              size="xs"
              mt="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY })}
            >
              Retry
            </Button>
          </Alert>
        ) : isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : (
          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <Select
                label="App"
                placeholder="Select an app to edit"
                data={selectData}
                value={selectedAppId}
                onChange={setSelectedAppId}
                searchable
                clearable
                nothingFoundMessage="No apps in the catalogue"
              />

              {!draft ? (
                <Text size="sm" c="dimmed">
                  Select an app above to edit its entitlement and feature flags.
                </Text>
              ) : (
                <>
                  {/* Entitlement */}
                  <Divider
                    label={
                      <Group gap={6}>
                        <IconShieldCheck size={14} />
                        <span>App-level entitlement</span>
                      </Group>
                    }
                    labelPosition="left"
                  />
                  <Text size="xs" c="dimmed">
                    An empty predicate means every authenticated user sees the app. When several
                    lists are set they are combined with AND; within each list it's OR.
                  </Text>
                  <StringListEditor
                    label="Any of roles"
                    description="Caller must hold at least one of these roles."
                    placeholder="Staff"
                    values={entitlement.anyOfRoles ?? []}
                    onChange={(v) =>
                      updateDraft({ entitlement: { ...entitlement, anyOfRoles: v } })
                    }
                  />
                  <StringListEditor
                    label="Any of rights"
                    description="Caller must hold at least one of these rights."
                    placeholder="tasks.view"
                    values={entitlement.anyOfRights ?? []}
                    onChange={(v) =>
                      updateDraft({ entitlement: { ...entitlement, anyOfRights: v } })
                    }
                  />
                  <StringListEditor
                    label="All of rights"
                    description="Caller must hold every one of these rights."
                    placeholder="tasks.admin"
                    values={entitlement.allOfRights ?? []}
                    onChange={(v) =>
                      updateDraft({ entitlement: { ...entitlement, allOfRights: v } })
                    }
                  />

                  {/* Feature flags */}
                  <Divider
                    label={
                      <Group gap={6}>
                        <IconFlag size={14} />
                        <span>Feature flags</span>
                      </Group>
                    }
                    labelPosition="left"
                  />
                  <Group gap="xs">
                    <TextInput
                      placeholder="task.ai-prioritizer"
                      value={flagKey}
                      onChange={(e) => setFlagKey(e.currentTarget.value)}
                      style={{ flex: 1 }}
                    />
                    <ActionIcon
                      variant="light"
                      aria-label="Add feature flag"
                      onClick={() => {
                        const k = flagKey.trim();
                        if (k && !(k in featureFlags)) {
                          updateDraft({ featureFlags: { ...featureFlags, [k]: true } });
                        }
                        setFlagKey('');
                      }}
                    >
                      <IconPlus size={16} />
                    </ActionIcon>
                  </Group>
                  {Object.keys(featureFlags).length === 0 ? (
                    <Text size="xs" c="dimmed">
                      No feature flags.
                    </Text>
                  ) : (
                    <Stack gap={4}>
                      {Object.entries(featureFlags).map(([key, enabled]) => (
                        <Group key={key} justify="space-between">
                          <Text size="sm">{key}</Text>
                          <Group gap="xs">
                            <Switch
                              checked={enabled}
                              onChange={(e) =>
                                updateDraft({
                                  featureFlags: {
                                    ...featureFlags,
                                    [key]: e.currentTarget.checked,
                                  },
                                })
                              }
                            />
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="red"
                              aria-label={`Remove flag ${key}`}
                              onClick={() => {
                                const next = { ...featureFlags };
                                delete next[key];
                                updateDraft({ featureFlags: next });
                              }}
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Group>
                        </Group>
                      ))}
                    </Stack>
                  )}

                  <Group justify="flex-end" mt="sm">
                    <Button
                      variant="subtle"
                      onClick={() => setDraft(selectedApp ? cloneManifest(selectedApp) : null)}
                      disabled={saving}
                    >
                      Reset
                    </Button>
                    <Button
                      leftSection={<IconDeviceFloppy size={16} />}
                      onClick={handleSave}
                      loading={saving}
                    >
                      Save Changes
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
    </FadeIn>
  );
}
